import { Clock, ExternalLink, MapPin, Phone } from 'lucide-react'
import type { PickupInfo } from '@/types'
import { groupPickupHours, pickupCityLine, pickupMapsUrl } from '@/lib/pickupHours'
import { cn } from '@/lib/cn'

/**
 * Where and when to collect: the shop's name, address, phone, opening hours
 * grouped by identical times, the admin's note and a map link. Shared by the
 * checkout and both order pages, so it stays in the storefront's neutral
 * recipe (no tinted chips) and renders whether or not pickup is currently
 * offered - an order placed while it was still on keeps its details.
 */
export default function PickupDetails({ pickup, className }: { pickup: PickupInfo; className?: string }) {
  const cityLine = pickupCityLine(pickup)
  const hours = groupPickupHours(pickup)
  const mapsUrl = pickupMapsUrl(pickup)

  return (
    <div className={cn('rounded-lg border border-foreground/10 bg-foreground/[0.03] p-3 text-sm', className)}>
      <p className="flex items-center gap-2 font-bold">
        <MapPin className="size-4 shrink-0 text-primary" /> {pickup.name}
      </p>
      {pickup.address && <p className="mt-1 whitespace-pre-line text-xs text-foreground/70">{pickup.address}</p>}
      {cityLine && <p className="text-xs text-foreground/70">{cityLine}</p>}
      {pickup.phone && (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground/70">
          <Phone className="size-3 shrink-0" /> {pickup.phone}
        </p>
      )}

      {hours.length > 0 && (
        <div className="mt-2.5 border-t border-foreground/10 pt-2.5">
          <p className="flex items-center gap-1.5 text-xs font-semibold">
            <Clock className="size-3 shrink-0" /> Pickup hours
          </p>
          <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-xs">
            {hours.map((row) => (
              <div key={row.days} className="contents">
                <dt className="font-semibold">{row.days}</dt>
                <dd className="text-foreground/70">{row.hours || 'By appointment'}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {pickup.note && <p className="mt-2 text-xs font-semibold text-foreground/80">{pickup.note}</p>}

      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          <ExternalLink className="size-3" /> Open in Google Maps
        </a>
      )}
    </div>
  )
}
